/**
 * BM-H278. One rule, one place, for "may this screen offer Send OB?"
 *
 * Vanessa, 4 Sep 2026: "if OB satisfied by 1. returned 2. marked as
 * skipped/linked/returned offline -> remove 'send OB' from the screen, it's
 * confusing and confused".
 *
 * This does NOT decide what counts as satisfied. The backend decides that
 * (hub/ob_send_guard.py, which delegates to hub/pipeline.ob_satisfied) and
 * ships the answer on every pipeline row as ob_satisfied plus a plain-words
 * ob_send_withheld_reason. This helper prefers those server fields and only
 * falls back to reading the row's own markers when a surface has not been
 * migrated to the enriched payload yet.
 *
 * conflict_cleared is the stub minted when a conflict clears. It is NOT
 * satisfied. A conflict_cleared matter is exactly the matter that still needs
 * its onboarding form, so it must keep the action.
 */

// Statuses that mean the OB is done, mirroring OB_SATISFIED_STATUSES and
// OB_SEND_WITHHELD_EXTRA_STATUSES in hub/ob_send_guard.py.
export const OB_SEND_WITHHELD_STATUSES = [
  "complete",
  "overridden",
  "waived_by_link",
  "skipped",
];

// Named so nobody widens the rule by accident: this status looks final and
// is not. Kept here as an explicit exclusion, never in the list above.
export const OB_NOT_SATISFIED_STUB_STATUS = "conflict_cleared";

/**
 * Return the plain-words reason Send OB must not be offered, or null when the
 * matter genuinely still needs its onboarding form.
 *
 * @param {object} row a pipeline summary item, a /matter/{ref}/status body, or
 *   a raw client_onboarding row.
 * @returns {string|null}
 */
export function obSendWithheldReason(row) {
  if (!row) return null;

  // Preferred path: the backend already answered.
  if (typeof row.ob_send_withheld_reason === "string" && row.ob_send_withheld_reason) {
    return row.ob_send_withheld_reason;
  }
  if (row.ob_satisfied === false) return null;

  const status = String(row.status || row.ob_status || "").trim();
  if (status === OB_NOT_SATISFIED_STUB_STATUS) return null;

  const linkedRef =
    row.linked_ob_matter_reference || row.ob_linked_from_matter_reference || null;
  if (linkedRef || row.completed_via === "link_ob_inheritance" || status === "waived_by_link") {
    return linkedRef
      ? `OB was satisfied via linked matter ${linkedRef}, so there is nothing to send.`
      : "OB was satisfied via a linked matter, so there is nothing to send.";
  }
  if (row.ob_returned_offline) {
    return "OB was returned offline and recorded on this matter, so there is nothing to send.";
  }
  if (row.ob_skipped || status === "skipped" || status === "overridden") {
    return "OB was skipped on this matter, so there is no onboarding questionnaire to send.";
  }
  if (row.ob_returned_at || status === "complete") {
    return "OB is already complete on this matter, so there is nothing to send.";
  }
  if (row.tob_accepted === true) {
    return "The client has accepted the terms of business on this matter, so there is nothing to send.";
  }
  return null;
}

/** True when this screen may still offer Send OB / Resend OB / Remind. */
export function canOfferSendOB(row) {
  return obSendWithheldReason(row) === null;
}
